import { useState } from "react";
import useSWR, { mutate } from "swr";
import { useSearchParams } from "react-router-dom";
import { Save, Search } from "lucide-react";
import { toast } from "sonner";
import { useAdminHeader } from "../AdminShell";
import { apiGet, apiGetEnvelope, apiSend, encodePk } from "../api";
import type { TableInfo } from "../types";
import { DynamicField, formatCell, PageError, PAGE_SIZE } from "../ui-helpers-extra";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const fetcher = <T,>(path: string) => apiGet<T>(path);

function primaryKeyFor(table: TableInfo, row: Record<string, unknown>) {
  return Object.fromEntries(table.primaryKey.map((field) => [field, row[field]]));
}

function rowKeyFor(table: TableInfo | undefined, row: Record<string, unknown>, fallback: string) {
  if (!table || table.primaryKey.length === 0) return fallback;
  return encodePk(primaryKeyFor(table, row));
}

export function DataBrowserPage() {
  const { data: tables, error } = useSWR<TableInfo[]>("/admin/api/tables", fetcher);
  const [params, setParams] = useSearchParams();
  const tableId = params.get("table") ?? "cities";
  const filter = params.get("filter") ?? "";
  const page = Math.max(1, Number(params.get("page") ?? "1") || 1);
  const offset = (page - 1) * PAGE_SIZE;
  const rowsPath = `/admin/api/tables/${tableId}/rows?limit=${PAGE_SIZE}&offset=${offset}&filter=${encodeURIComponent(filter)}`;
  const { data: envelope, error: rowsError } = useSWR(rowsPath, () => apiGetEnvelope<Record<string, unknown>[]>(rowsPath));
  const rows = envelope?.data ?? [];
  const table = tables?.find((item) => item.id === tableId);
  const [selected, setSelected] = useState<Record<string, unknown> | null>(null);
  const [tableQuery, setTableQuery] = useState("");
  const columns = Object.keys(rows[0] ?? {}).slice(0, 10);
  const total = Number(envelope?.meta?.count ?? rows.length);
  const normalizedTableQuery = tableQuery.trim().toLowerCase();
  const visibleTables = tables?.filter((item) => {
    if (!normalizedTableQuery) return true;
    const searchFields = {
      description: item.description ?? "",
      id: item.id,
      label: item.label,
    };
    return Object.values(searchFields).some((value) => value.toLowerCase().includes(normalizedTableQuery));
  }) ?? [];
  function patch(next: Record<string, string | null>) {
    const out = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) value ? out.set(key, value) : out.delete(key);
    setParams(out);
  }
  useAdminHeader({
    title: table?.label ?? "Data",
    breadcrumbs: [{ label: "Data" }],
    actions: <div className="relative w-80"><Search className="absolute left-2 top-2 size-4 text-muted-foreground" /><Input className="pl-8" defaultValue={filter} onKeyDown={(event) => { if (event.key === "Enter") patch({ filter: event.currentTarget.value, page: "1" }); }} placeholder="Filter rows" /></div>,
  }, [filter, table?.label]);
  return (
    <div className="grid h-full grid-cols-[260px_minmax(0,1fr)] overflow-hidden">
      <aside className="flex min-h-0 flex-col border-r">
        <div className="border-b p-3">
          <div className="relative">
            <Search className="absolute left-2 top-2 size-4 text-muted-foreground" />
            <Input
              aria-label="Search tables"
              className="pl-8"
              onChange={(event) => setTableQuery(event.target.value)}
              placeholder="Search tables"
              value={tableQuery}
            />
          </div>
        </div>
        <ScrollArea className="min-h-0 flex-1">
          <div className="grid gap-1 p-3 pr-2">
            {visibleTables.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`rounded-md px-2 py-2 text-left hover:bg-muted ${item.id === tableId ? "bg-muted" : ""}`}
                onClick={() => {
                  patch({ table: item.id === "cities" ? null : item.id, page: "1" });
                  setSelected(null);
                }}
              >
                <div className="font-medium">{item.label}</div>
                <div className="text-xs text-muted-foreground">{item.rowCount.toLocaleString()} rows</div>
              </button>
            ))}
            {tables && visibleTables.length === 0 ? <div className="px-2 py-6 text-sm text-muted-foreground">No tables match.</div> : null}
          </div>
        </ScrollArea>
      </aside>
      <section className="min-w-0 overflow-y-auto p-4">
        {(error || rowsError) && <PageError error={error || rowsError} />}
        <div className="mb-4">
          <h1 className="text-lg font-semibold">{table?.label ?? "Data"}</h1>
          {table?.description ? <p className="mt-1 text-sm text-muted-foreground">{table.description}</p> : null}
        </div>
        {!envelope ? (
          <Skeleton className="h-96" />
        ) : (
          <>
            <div className="overflow-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>{columns.map((key) => <TableHead key={key}>{key}</TableHead>)}</TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row, index) => {
                    const rowKey = rowKeyFor(table, row, String(index));
                    return (
                      <TableRow
                        key={rowKey}
                        className="cursor-pointer"
                        data-state={row === selected ? "selected" : undefined}
                        onClick={() => setSelected(row)}
                      >
                        {columns.map((key) => <TableCell key={key}>{formatCell(row[key])}</TableCell>)}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <div className="mt-3 flex items-center justify-between text-sm text-muted-foreground">
              <span>{offset + 1}-{Math.min(offset + rows.length, total)} of {total}</span>
              <div className="flex gap-2">
                <Button type="button" variant="outline" disabled={page <= 1} onClick={() => patch({ page: String(page - 1) })}>Previous</Button>
                <Button type="button" variant="outline" disabled={offset + PAGE_SIZE >= total} onClick={() => patch({ page: String(page + 1) })}>Next</Button>
              </div>
            </div>
          </>
        )}
      </section>
      <RowInspector
        table={table}
        row={selected}
        rowsPath={rowsPath}
        onClose={() => setSelected(null)}
        onDone={() => {
          mutate(rowsPath);
          setSelected(null);
        }}
      />
    </div>
  );
}

function RowInspector({ table, row, rowsPath, onClose, onDone }: { table?: TableInfo; row: Record<string, unknown> | null; rowsPath: string; onClose: () => void; onDone: () => void }) {
  if (!table || !row) return null;
  const rowKey = rowKeyFor(table, row, "selected-row");
  return (
    <Sheet open onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent side="right" className="w-full !max-w-none gap-0 overflow-hidden p-0 data-[side=right]:!w-full data-[side=right]:!max-w-none md:data-[side=right]:!min-w-[480px] md:data-[side=right]:!w-[50vw] md:data-[side=right]:!max-w-[760px]">
        <RowInspectorContent key={rowKey} table={table} row={row} rowsPath={rowsPath} onDone={onDone} />
      </SheetContent>
    </Sheet>
  );
}

function RowInspectorContent({ table, row, rowsPath, onDone }: { table: TableInfo; row: Record<string, unknown>; rowsPath: string; onDone: () => void }) {
  const [draft, setDraft] = useState<Record<string, unknown>>(() => ({ ...row }));
  const currentTable = table;
  const readOnly = currentTable.access === "read-only";
  const fieldSets = {
    json: new Set(currentTable.jsonFields ?? []),
    readonly: new Set(currentTable.readonlyFields ?? []),
  };
  const pk = primaryKeyFor(currentTable, row);
  const path = `/admin/api/tables/${currentTable.id}/rows/${encodePk(pk)}`;
  const keys = Object.keys(row);
  async function save() {
    const writable: Record<string, unknown> = {};
    for (const key of keys) {
      if (!fieldSets.readonly.has(key) && !fieldSets.json.has(key)) writable[key] = draft[key];
    }
    await apiSend(path, "PATCH", writable);
    toast.success("Row saved");
    mutate(rowsPath);
    onDone();
  }
  return (
    <>
      <SheetHeader className="shrink-0 border-b pr-12">
        <SheetTitle>{currentTable.label}</SheetTitle>
        <SheetDescription>{readOnly ? "Read only" : "Editable"}</SheetDescription>
      </SheetHeader>
      <ScrollArea className="min-h-0 flex-1">
        <div className="grid gap-3 p-4 pr-2">
          {keys.map((key) => (
            <DynamicField
              key={key}
              name={key}
              value={draft[key]}
              readOnly={readOnly || fieldSets.readonly.has(key)}
              json={fieldSets.json.has(key)}
              onChange={(value) => setDraft((currentDraft) => ({ ...currentDraft, [key]: value }))}
            />
          ))}
        </div>
      </ScrollArea>
      <SheetFooter className="shrink-0 border-t">
        <Button type="button" className="w-full" disabled={readOnly} onClick={() => save().catch((error) => toast.error(error instanceof Error ? error.message : "Save failed"))}>
          <Save /> Save row
        </Button>
      </SheetFooter>
    </>
  );
}
